import ReactCrop, { type Crop, makeAspectCrop } from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
import { Modal } from "../../Modal/Modal"
import * as React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "../../Button/Button"
import { CropOptions, CropType, ImageOptions } from "../types"
import { classNames } from "../../../../utils/classNames"
import {
    getCropOutput,
    getCurrentCropEditData,
    initCropAreaDimensions,
    mutateCropResultData,
    prepareEditData,
    updateCropImagesState,
} from "./utils"
import { CroppedVariantEdit, ImageCropEditData, ImageCropInputData, ImageCropResultData } from "./types"
import { useCleanupImageUrls } from "./hooks"
import { getAspectRatioFromCropType } from "../utils"

type Props = {
    imagesToCrop: ImageCropInputData[]
    title?: string
    description?: string
    cropOptions?: CropOptions[]
    imageOptions?: ImageOptions
    showSizeWarning?: boolean
    onConfirm: (croppedData: ImageCropResultData[]) => Promise<void>
    onCancel: () => void
}

const DEFAULT_CROP_OPTIONS: CropOptions[] = [{ cropType: CropType.Free }]

export const CropModal = ({
    title = "Crop image",
    description = "Drag handles to adjust crop area",
    cropOptions = DEFAULT_CROP_OPTIONS,
    ...props
}: Props) => {
    // Store current image index (for multiple images cropping)
    const [currentImgIdx, setCurrentImgIdx] = useState<number>(0)

    // Store current crop ratio index
    const [currentCropRatioIdx, setCurrentCropRatioIdx] = useState<number>(0)

    // Store edit data (crop areas, etc.)
    const [editData, updateEditData] = useState<ImageCropEditData[]>(() =>
        prepareEditData(props.imagesToCrop, cropOptions)
    )

    // Get current data based on current image/crop-step idx
    const { currentFile, currentCropCoords, currentCropOption, currentCropData, currentImgUrl, currentAspect } =
        useMemo(
            () => getCurrentCropEditData(editData, currentImgIdx, currentCropRatioIdx),
            [editData, currentCropRatioIdx, currentImgIdx]
        )

    // When image is cropped and confirmed, store the result data
    const resultData = useRef<ImageCropResultData[]>([])

    // Track if this is the initial mount to skip duplicate prepareEditData call
    const isInitialMount = useRef(true)

    // Update edit data and reset steps if new props come in (skip initial mount since useState already handles it)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false
            return
        }
        updateEditData(prepareEditData(props.imagesToCrop, cropOptions))
        setCurrentImgIdx(0)
        setCurrentCropRatioIdx(0)
    }, [cropOptions, props.imagesToCrop])

    // On unmount, revoke all object URLs to avoid memory leaks
    useCleanupImageUrls(editData)

    if (!currentImgUrl || !currentCropOption) {
        return null
    }

    const updateCropEditData = (newData: Partial<CroppedVariantEdit>, imageIdx: number, cropIdx: number) => {
        updateCropImagesState(newData, imageIdx, cropIdx, updateEditData)
    }

    const initCropArea = (e: React.SyntheticEvent<HTMLImageElement>) => {
        if (currentCropData) {
            // Skip if crop area already exists
            return
        }

        const imgEl = e.currentTarget
        const {
            width: imgScreenWidth,
            height: imgScreenHeight,
            naturalWidth: imgWidth,
            naturalHeight: imgHeight,
        } = imgEl

        const { cropXPerc, cropYPerc, cropWidthPerc, cropHeightPerc } = initCropAreaDimensions(
            currentCropOption,
            currentCropCoords,
            imgWidth,
            imgHeight
        )

        // If there is aspect ratio defined, apply it to crop area dimensions
        if (currentAspect) {
            let cropData: Crop = makeAspectCrop(
                {
                    x: cropXPerc,
                    y: cropYPerc,
                    unit: "%",
                    width: cropWidthPerc,
                },
                currentAspect,
                imgScreenWidth,
                imgScreenHeight
            )

            // If there is no previously saved crop coords, initialize the crop area in the center of the image
            cropData = {
                ...cropData,
                x: !currentCropCoords ? (100 - cropData.width) / 2 : cropData.x,
                y: !currentCropCoords ? (100 - cropData.height) / 2 : cropData.y,
            }

            updateCropEditData({ cropData: cropData }, currentImgIdx, currentCropRatioIdx)
            return
        }

        const cropData: Crop = {
            unit: "%",
            x: cropXPerc,
            y: cropYPerc,
            width: cropWidthPerc,
            height: cropHeightPerc,
        }
        updateCropEditData({ cropData: cropData }, currentImgIdx, currentCropRatioIdx)
    }

    const onCropConfirm = async (currentImgIdx: number, currentCropRatioIdx: number) => {
        if (!currentCropData) {
            return
        }
        const result = await getCropOutput(
            currentCropData,
            currentFile,
            currentImgUrl,
            currentCropOption,
            props.imageOptions ?? null
        )
        if (result) {
            // Store result data in resultData ref (mutate the data)
            mutateCropResultData(resultData.current, currentImgIdx, currentCropRatioIdx, currentFile, {
                ...result,
                cropType: currentCropOption.cropType,
            })
        }
    }

    const goToPreviousCropStep = () => {
        setCurrentCropRatioIdx(val => val - 1)
    }

    const goToNextCropStep = async () => {
        await onCropConfirm(currentImgIdx, currentCropRatioIdx)
        setCurrentCropRatioIdx(val => val + 1)
    }

    const goToCropStep = async (cropIdx: number) => {
        await onCropConfirm(currentImgIdx, currentCropRatioIdx)
        setCurrentCropRatioIdx(cropIdx)
    }

    const goToNextImage = async () => {
        await onCropConfirm(currentImgIdx, currentCropRatioIdx)
        if (currentImgIdx < props.imagesToCrop.length - 1) {
            // If not last image, go to next image
            setCurrentImgIdx(val => val + 1)
            setCurrentCropRatioIdx(0)
        } else {
            // If last image, confirm and close
            props.onConfirm(resultData.current)
        }
    }

    const renderButtons = () => {
        const isOnLastCropStep = currentCropRatioIdx === (cropOptions?.length ?? 1) - 1
        if (isOnLastCropStep) {
            return (
                <div className={classes.btnsWrap1}>
                    <div className={classes.btnsWrap2}>
                        {currentCropRatioIdx > 0 ? (
                            <Button className={classes.actionBtn} onClick={goToPreviousCropStep}>
                                Back
                            </Button>
                        ) : (
                            <Button className={classes.actionBtn} onClick={props.onCancel}>
                                Cancel
                            </Button>
                        )}
                        <Button type="primary" key="confirm" className={classes.actionBtn} onClick={goToNextImage}>
                            Confirm
                        </Button>
                    </div>
                </div>
            )
        }
        return (
            <div className={classes.btnsWrap1}>
                <div className={classes.btnsWrap2}>
                    <Button className={classes.actionBtn} onClick={props.onCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" key="next" className={classes.actionBtn} onClick={goToNextCropStep}>
                        Next
                    </Button>
                </div>
            </div>
        )
    }

    const onCropAreaChange = (pixelCrop: Crop, percentCrop: Crop) => {
        updateCropEditData({ cropData: percentCrop }, currentImgIdx, currentCropRatioIdx)
    }

    const renderContent = () => {
        return (
            <div>
                {currentCropOption.cropDescription ? (
                    <div className={classes.label}>{currentCropOption.cropDescription}</div>
                ) : null}
                {props.showSizeWarning && props.imageOptions && (
                    <div className={classes.warnMsg}>
                        Your image is smaller than recommended size {props.imageOptions?.minWidth}x
                        {props.imageOptions?.minHeight}. Please note that this can lead to unexpected visual results.
                    </div>
                )}
                <ReactCrop
                    key={currentImgIdx + "-" + currentCropRatioIdx} // Reset crop when image or crop option changes
                    className={classes.cropEl}
                    crop={currentCropData ?? undefined}
                    aspect={currentAspect ?? undefined}
                    locked={currentCropOption.locked}
                    ruleOfThirds={true}
                    keepSelection={true}
                    onChange={onCropAreaChange}>
                    <img src={currentImgUrl} alt="Cropped image" onLoad={initCropArea} />
                </ReactCrop>
                <div className="mb-2">{description}</div>
                {renderButtons()}
            </div>
        )
    }

    const renderCropSteps = () => {
        return (
            <div className={classes.stepsWrap}>
                {cropOptions.map((opt, idx) => {
                    const isActive = idx === currentCropRatioIdx
                    const aspectRatio = opt.cropType ? getAspectRatioFromCropType(opt.cropType) : null
                    return (
                        <button
                            type="button"
                            key={idx}
                            onClick={() => goToCropStep(idx)}
                            className={classNames(
                                classes.stepBtn,
                                isActive ? "border-primary-color" : "border-gray-200"
                            )}>
                            <div className={classes.stepRatioRect}>
                                <div
                                    className={classNames(
                                        classes.stepRatioRect2,
                                        isActive ? "bg-primary-color" : "bg-gray-200"
                                    )}
                                    style={{
                                        aspectRatio: aspectRatio ?? "auto",
                                    }}
                                />
                            </div>
                            <div className="flex-1">
                                <div className={classes.stepLabel}>Step {idx + 1}</div>
                                {opt.stepLabel ? <div className={classes.stepLabel2}>{opt.stepLabel}</div> : null}
                            </div>
                        </button>
                    )
                })}
            </div>
        )
    }

    const showCropSteps = cropOptions.length > 1
    return (
        <Modal isVisible={true} onClose={props.onCancel} size={showCropSteps ? "largeXL" : "large"}>
            <div className={classes.title}>{title}</div>
            <div className={classes.wrap}>
                {showCropSteps ? <div>{renderCropSteps()}</div> : null}
                <div>{renderContent()}</div>
            </div>
        </Modal>
    )
}

const classes = {
    wrap: "-m-1 flex leading-5",
    title: "-ml-1 mb-5 mt-1 text-22 font-bold",
    label: "mb-3 text-[14px] text-black/70",
    cropEl: "mb-2 border border-gray-200 bg-white",
    btnsWrap1: "flex justify-end",
    btnsWrap2: "flex w-full max-w-[240px] space-x-2",
    actionBtn: "flex-1",
    warnMsg: "mb-4 -mt-2 rounded-lg border border-orange-500/70 bg-orange-500/10 py-2 px-3",
    stepsWrap: "w-[250px] space-y-4 mr-8",
    stepBtn: "h-[66px] border rounded-[4px] flex items-center bg-transparent text-left w-full cursor-pointer",
    stepRatioRect: "w-[80px] flex justify-center",
    stepRatioRect2: "h-[20px] rounded-[2px]",
    stepLabel: "text-[16px] font-bold",
    stepLabel2: "text-[14px] text-gray-500",
}
